import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import oktaconfig from "@appcore/app.config";
import { HubUser, OktaUser } from "@appcore/models/user";
import { AuthenticationService } from "./authentication.service";
import { ColorUtility } from "@service/system/utilities.service";
import { UserService } from "@service/user.service";
import { BrowserStorage } from "@appcore/_helpers/browserstorage";
import { AccessToken, IDToken, OktaAuth } from "@okta/okta-auth-js";
import { environment } from "environments/environment";
import { BehaviorSubject, Observable, Observer, throwError } from "rxjs";

@Injectable({ providedIn: "root" })
export class OktaAuthService implements AuthenticationService {
	// carryovers from the interface
	currentUser: Observable<any>;
	adminRoleLevel;

	oktaAuth = new OktaAuth({
		clientId: process.env.CLIENT_ID,
		issuer: process.env.ISSUER,
		pkce: true
	});

	$userName = "";
	$isAuthenticated: Observable<boolean>;
	$authState: boolean;
	$authed = new BehaviorSubject<boolean>(false);
	private observer?: Observer<boolean>;
	public expirationTime: number;
	public currentTime = new Date().getTime();
	public timeRemaining: number;

	constructor(
		private storageService: BrowserStorage,
		private colorUtil: ColorUtility,
		private oktaBare: OktaAuth,
		private router: Router,
		private userService: UserService
	) {
		this.$isAuthenticated = new Observable((observer: Observer<boolean>) => {
			this.observer = observer;
			this.isAuthenticated().then(val => {
				observer.next(val);
				return val;
			});
			if (!this.isAuthenticated()) {
				console.log(`Invalid Session.`);
				this.logout();
			}
		});
	}

	async isAuthenticated(): Promise<boolean> {
		try {
			await this.oktaAuth.authStateManager.updateAuthState();
			// Checks if there is a valid accessToken and idToken in the TokenManger.
			const isValidAccessToken = await this.oktaAuth.tokenManager.get("accessToken");
			const isValidIdToken = await this.oktaAuth.tokenManager.get("idToken");
			if (isValidAccessToken && isValidIdToken) {
				this.$authed.next(true);
				return true;
			} else {
				if (!isValidAccessToken) console.log("Invalid access token.");
				if (!isValidIdToken) console.log("Invalid id token.");
				this.$authed.next(false);
				return false;
			}
		} catch (err) {
			// localStorage['okta-token-storage'] is coming back as undefined.
			this.handleError;
		}
	}

	addAuthStateListeners(): void {
		this.oktaAuth.tokenManager.on("expired", key => {
			console.log(`Event 'expired': ${key}`);
			this.currentUserValue();
		});

		this.oktaAuth.authStateManager.subscribe(response => {
			this.$authState = response.isAuthenticated;
		});

		this.oktaAuth.tokenManager.on("added", () => {
			console.log("TokenManager: Token added.");
		});

		this.oktaAuth.tokenManager.on("renewed", function (key, newToken, oldToken) {
			console.log("Token with key", key, "has been renewed");
			console.log("Old token:", oldToken);
			console.log("New token:", newToken);
		});

		this.oktaAuth.tokenManager.on("removed", () => {
			this.logout();
			console.log("TokenManager: Token removed.");
		});
	}

	extendSession(): void {
		console.log(`Requesting a new token.`);
		this.oktaAuth.tokenManager.renew("accessToken");
	}

	getCurrentUserValue(): Observable<HubUser> {
		return AuthenticationService.currentUserSubject;
	}

	/**
	 * @author     John Ying <john.ying@childrenshospitals.org>
	 * @modified   8/29/2023
	 *
	 * Gets the {@link OktaUser} info and returns a subscribable AsyncSubject {@link HubUser}.
	 *
	 * NOTE: Due to the Okta api interface restrictions and a desire to databind the
	 * HubUser data, the current user value actual return is not a Promise.
	 * It has been internally reformed to be a subscribable AsyncSubject.
	 *
	 * @returns    A subscribable AsyncSubject {@link HubUser}
	 */
	async currentUserValue(): Promise<OktaUser> {
		let myUser: HubUser = new HubUser();

		if (await this.isAuthenticated()) {
			const userClaims = await this.oktaAuth.getUser();
			myUser.UserName = userClaims.preferred_username;
			myUser.FirstName = userClaims.given_name;
			myUser.LastName = userClaims.family_name;
			myUser.Groups = (userClaims.Groups as string).toString();
			myUser.Organization = userClaims.OrganizationId;
			myUser.Initials = myUser.FirstName.substring(0, 1) + myUser.LastName.substring(0, 1);
			myUser.ChipColor = this.colorUtil.colorStringToHSLHex(myUser.UserName);

			const hubUser = await this.userService.getUser(myUser.UserName).toPromise();
			myUser = Object.assign({}, hubUser[0], myUser);

			AuthenticationService.currentUserSubject.next(myUser);
			AuthenticationService.currentUserSubject.complete();
		}

		return myUser;
	}

	async login(currRouteUrl?: string): Promise<boolean> {
		let complete = false;
		if (currRouteUrl != null || typeof currRouteUrl !== "undefined") {
			// Save current URL before redirect
			sessionStorage.setItem("okta-app-url", currRouteUrl || this.router.url);
		}

		/* For reference only...
			this.oktaAuth.token.getWithRedirect({
				scopes: ['openid', 'profile', 'email', 'crm_reader', 'phone', 'address'],
			});
		*/

		await this.oktaBare.signInWithRedirect({ originalUri: "/" });
		complete = true;

		return complete;
	}

	/**
	 * Refresh Token - Not the same as refresh access token.
	 */
	async refreshToken(): Promise<void> {
		this.oktaAuth.session.refresh();
	}

	async handleAuthentication(): Promise<void> {
		this.addAuthStateListeners();

		const tokenContainer = await this.oktaAuth.token.parseFromUrl();

		this.oktaAuth.tokenManager.add("idToken", tokenContainer.tokens.idToken as IDToken);
		this.oktaAuth.tokenManager.add("accessToken", tokenContainer.tokens.accessToken as AccessToken);

		if (await this.isAuthenticated()) {
			this.observer?.next(true);
			const reconciledUser = await this.currentUserValue();

			console.log(`Reconciling User Info...`);

			this.userService.reconcileUser(reconciledUser).subscribe(() => {
				// console.log(`User Info Reconciled for: ${JSON.stringify(reconciledUser)}`);
				console.log(`User Info Reconciled for: ${reconciledUser.UserName}`);
			});
		} else {
			console.log("Not authenticated. Returning to Login screen.");
			this.logout();
		}

		// Retrieve the saved URL and navigate back
		const url = sessionStorage.getItem("okta-app-url") as string;
		this.router.navigateByUrl(url);
	}

	async logout(): Promise<void> {
		const postLogoutURL = (oktaconfig.oidc.postLogoutRedirectUri = environment.fullBaseURI);

		/* For reference only...
			this.oktaAuth.tokenManager.clear();
			setTimeout(() => {
				window.location.href = environment.fullBaseURI;
			}, 500);
		*/

		// If we have a previously-set return url, let's remove it so it doesn't keep trying to send us back there.
		sessionStorage.removeItem("okta-app-url");
		// Will sign-out using Okta to end the session, then redirect back to the configured `postLogoutRedirectUri`
		await this.oktaAuth.signOut({ postLogoutRedirectUri: postLogoutURL });
	}
	// parase JSON object from base64 encoded JWT
	// const jwtToken = JSON.parse(atob(this.currentUserValue.jwtToken.split('.')[1]))

	handleError(error: { error: { message: any }; status: any; message: any }): Observable<never> {
		let errorMessage = "";
		if (error.error instanceof ErrorEvent) {
			// client-side error
			errorMessage = `Error: ${error.error.message}`;
		} else {
			// server-side error
			errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
		}
		console.log(errorMessage || error);
		return throwError(errorMessage);
	}
}
